import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { getZone, type Zone } from '../../../zones.api';

/** `zone` is null for a new zone. */
export type ZoneRecordState =
  { status: 'loading' } | { status: 'ready'; zone: Zone | null } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): ZoneRecordState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

/** The zone a panel edits (`new` needs no fetch). Mount with `key={zoneId}`: the state is set up once per zone. */
export function useZoneRecord(zoneId: string) {
  const isNew = zoneId === 'new';
  const [state, setState] = useState<ZoneRecordState>(isNew ? { status: 'ready', zone: null } : { status: 'loading' });

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    getZone(zoneId)
      .then((zone) => {
        if (!cancelled) setState({ status: 'ready', zone });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [zoneId, isNew]);

  /** Re-fetches after deactivating or reactivating, so the panel shows the new status. */
  async function reload(): Promise<void> {
    if (isNew) return;
    try {
      setState({ status: 'ready', zone: await getZone(zoneId) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, reload };
}
